import {startTransition, useCallback, useEffect, useMemo, useState, type ReactNode} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getAuditProgramSection,
    saveAuditProgramSectionPage,
} from '@/services/auditService/auditProgramService';
import {getAppendixDef} from '@/pages/components/audit-program/appendix';
import AppendixTableView from '@/pages/components/audit-program/appendix/AppendixTableView';
import EsSummaryView from '@/pages/components/audit-program/e-section/EsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';
import type {ESummaryPage} from '@/pages/components/audit-program/e-section/constants';
import type {G0Page} from '@/pages/components/audit-program/g-section/constants';

export type AppendixSectionViewProps = {
    appendixCode: string;
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: string;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function AppendixSectionView({
    appendixCode,
    companyId,
    annualId,
    companyName,
    initialPage,
}: AppendixSectionViewProps) {
    const def = getAppendixDef(appendixCode);
    const sectionCode = def?.sectionCode ?? `AP${appendixCode}`;
    const pages = def?.pages ?? [];
    const indexPage = pages[0] ?? '';
    const [pageKey, setPageKey] = useState(initialPage || indexPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates(sectionCode);

    const displayOnly = useMemo(() => new Set([indexPage]), [indexPage]);

    const load = useCallback(async () => {
        if (!def) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getAuditProgramSection(companyId, annualId, sectionCode);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const next: Record<string, Record<string, string>> = {};
            for (const code of def.pages) {
                if (code === indexPage) {
                    next[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                next[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(next);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load appendix';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName, def, sectionCode, indexPage]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        if (initialPage) setPageKey(initialPage);
        else if (indexPage) setPageKey(indexPage);
    }, [initialPage, indexPage]);

    const handleFieldChange = useCallback((pageCode: string, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!def || displayOnly.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveAuditProgramSectionPage({
                companyId,
                annualId,
                sectionCode,
                pageCode: pageKey,
                fieldData: pageData[pageKey] ?? {},
            });
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, sectionCode, dirty, pageKey, pageData, def, displayOnly]);

    const tabs = useMemo(() => {
        if (!def) return [];
        return def.pages.map((code) => {
            let content: ReactNode = null;
            if (header) {
                if (code === indexPage) {
                    const fromApi = getContent(code) as {rows?: typeof def.indexRows} | undefined;
                    const rows = fromApi?.rows?.length ? fromApi.rows : def.indexRows;
                    content = (
                        <SectionIndexView
                            header={header}
                            refCode={code}
                            sectionLetter={def.indexLetter}
                            sectionTitle={def.indexTitle}
                            rows={rows}
                            onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode))}
                        />
                    );
                } else if (def.summaries[code]) {
                    const page = (getContent(code) ?? def.summaries[code]) as ESummaryPage;
                    content = (
                        <EsSummaryView
                            header={header}
                            page={page}
                            fallback={def.summaries[code]}
                            refCode={code}
                            values={pageData[code] ?? {}}
                            onChange={(k, v) => handleFieldChange(code, k, v)}
                        />
                    );
                } else if (def.programmes[code]) {
                    const page = (getContent(code) ?? def.programmes[code]) as G0Page;
                    content = (
                        <G0ProgrammeView
                            header={header}
                            page={page}
                            refCode={code}
                            values={pageData[code] ?? {}}
                            onChange={(k, v) => handleFieldChange(code, k, v)}
                        />
                    );
                } else if (def.tables?.[code]) {
                    const page = def.tables[code];
                    content = (
                        <AppendixTableView
                            header={header}
                            page={page}
                            refCode={code}
                            values={pageData[code] ?? {}}
                            onChange={(k, v) => handleFieldChange(code, k, v)}
                        />
                    );
                }
            }
            return {
                key: code,
                label: <PageTabLabel code={code} title={def.pageTitles[code] ?? code}/>,
                icon: code,
                content,
            };
        });
    }, [def, header, pageData, handleFieldChange, getContent, indexPage]);

    if (!def) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="text.secondary">
                    Appendix {appendixCode} is not available in the Audit Practice Manual (no programme sheets).
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = displayOnly.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {def.indexLetter} — {def.title}
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={setPageKey}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
